import { useCallback, useRef, useState } from 'react';
import { Info } from 'lucide-react';

interface HelpIconTooltipProps {
  text: string;
  /** Alineación del panel respecto al icono (por defecto centrado; se ajusta al viewport al abrir). */
  align?: 'start' | 'center' | 'end';
  /** Lado preferido; se voltea si no hay espacio. */
  side?: 'top' | 'bottom';
}

type Align = 'start' | 'center' | 'end';
type Side = 'top' | 'bottom';

/**
 * Tooltip de ayuda discreto (mismo lenguaje visual que Salud del Inventario / FilterHelpIconPanel).
 * Al abrir, ajusta alineación y lado para no salirse del viewport.
 */
export function HelpIconTooltip({
  text,
  align: preferredAlign = 'center',
  side: preferredSide = 'bottom',
}: HelpIconTooltipProps) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [align, setAlign] = useState<Align>(preferredAlign);
  const [side, setSide] = useState<Side>(preferredSide);

  const reposition = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const margin = 16;
    const panelW = Math.min(20 * 16, window.innerWidth - margin * 2);
    const estimatedH = Math.min(220, Math.ceil(text.length / 42) * 16 + 24);

    let nextAlign: Align = preferredAlign;
    const centerLeft = rect.left + rect.width / 2 - panelW / 2;
    const centerRight = centerLeft + panelW;
    if (centerRight > window.innerWidth - margin) nextAlign = 'end';
    else if (centerLeft < margin) nextAlign = 'start';

    let nextSide: Side = preferredSide;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    if (preferredSide === 'bottom' && spaceBelow < estimatedH && spaceAbove > spaceBelow) {
      nextSide = 'top';
    } else if (preferredSide === 'top' && spaceAbove < estimatedH && spaceBelow > spaceAbove) {
      nextSide = 'bottom';
    }

    setAlign(nextAlign);
    setSide(nextSide);
  }, [preferredAlign, preferredSide, text.length]);

  const panelPosition =
    align === 'start' ? 'left-0' : align === 'end' ? 'right-0' : 'left-1/2 -translate-x-1/2';
  const sidePosition =
    side === 'top' ? 'bottom-full mb-0.5 pb-1' : 'top-full mt-0.5 pt-1';

  return (
    <div
      ref={anchorRef}
      className="group relative inline-flex shrink-0"
      onMouseEnter={reposition}
      onFocus={reposition}
    >
      <button
        type="button"
        className="inline-flex cursor-help rounded p-0.5 text-gray-400 outline-none hover:text-blue-600 focus-visible:ring-2 focus-visible:ring-[#1681ba] focus-visible:ring-offset-1"
        aria-label={text}
      >
        <Info className="h-3.5 w-3.5" aria-hidden />
      </button>
      <div
        className={`pointer-events-none invisible absolute z-[60] w-max max-w-[min(20rem,calc(100vw-2rem))] opacity-0 transition-opacity duration-75 group-hover:pointer-events-auto group-hover:visible group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:visible group-focus-within:opacity-100 ${panelPosition} ${sidePosition}`}
        role="tooltip"
      >
        <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-left text-[11px] leading-snug text-gray-600 shadow-lg ring-1 ring-black/5">
          {text}
        </div>
      </div>
    </div>
  );
}
